<template>
	<SuiAutoSuggest
		:id="id"
		:value="activeItem ? activeItem.id : undefined"
		:auto-width="autoWidth"
		:active-item="activeItem"
		:options="options"
		:is-loading="isLoading"
		:auto-navigation="autoNavigation"
		:cache-options="false"
		:name="name"
		:format-title="formatTitle"
		:placeholder="placeholder"
		:loading-text="$t('spa__general.loading')"
		:variant="variant"
		:message="message"
		:min-width="minWidth"
		:max-width="maxWidth"
		:append-to="appendTo"
		:enable-filtering="enableFiltering"
		size="small"
		:dropdown-class-addons="dropdownClassAddons"
		@input="onItemSelected"
		@load="$emit('load', $event)"
	>
		<template #no-results-content>
			{{ $t('search.noResults') }}
		</template>
		<template v-if="$slots.footer" #footer>
			<slot name="footer" />
		</template>
	</SuiAutoSuggest>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { SuiAutoSuggest, SuiInputVariant } from '@developers/scoro-ui';
import type { SelectItem } from '@developers/scoro-ui';
import { type ValueOf } from '@/types';
import { isValueOf } from '@/utils/propValidation';
import { useMfeUtils } from '@/composables';
import { AutoSuggestDefaultEmits } from './types';

export default defineComponent({
	components: {
		SuiAutoSuggest,
	},

	props: {
		/**
		 * Selected dropdown item to display without fetching all list
		 */
		activeItem: {
			type: Object as PropType<SelectItem>,
			default: undefined,
		},

		isLoading: {
			type: Boolean,
			required: true,
		},

		/**
		 * Dropdown options for select
		 */
		options: {
			type: Array as PropType<SelectItem[]>,
			required: true,
		},

		/**
		 * Function for formating dropdown items
		 */
		formatTitle: {
			type: Function as PropType<(item: SelectItem) => string>,
			default: (item: SelectItem) => {
				return item.title;
			},
		},

		placeholder: {
			type: String,
			default: undefined,
		},

		id: {
			type: String,
			default: 'autoSuggest',
		},

		name: {
			type: String,
			default: undefined,
		},

		autoNavigation: {
			type: Boolean,
			required: false,
			default: false,
		},

		autoWidth: {
			type: Boolean,
			default: false,
		},

		variant: {
			type: String as PropType<
				ValueOf<typeof SuiInputVariant> | undefined
			>,
			default: undefined,
			validator: isValueOf(SuiInputVariant),
		},

		message: {
			type: String,
			default: undefined,
		},

		minWidth: {
			type: String,
			default: '16rem',
		},

		maxWidth: {
			type: String,
			default: '100%',
		},

		appendTo: {
			type: String,
			default: 'body',
		},

		enableFiltering: {
			type: Boolean,
			default: true,
		},

		dropdownClassAddons: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
	},

	emits: AutoSuggestDefaultEmits,

	setup(props, { emit }) {
		const { rootEmit } = useMfeUtils();

		const onItemSelected = (item: SelectItem) => {
			emit('input', item);
			if (item?.id) {
				rootEmit('auto-suggest-selected', {
					id: item.id,
					name: item.title,
				});
			} else {
				rootEmit('auto-suggest-cleared');
			}
		};

		return {
			onItemSelected,
		};
	},
});
</script>
